import { createFileRoute } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { useRouteMeta } from '@/lib/use-route-meta';

export const Route = createFileRoute('/privacidade')({ component: PrivacidadePage });

const LAST_UPDATED = '30 de abril de 2025';
const CONTACT_EMAIL = 'privacidade@elocomum.com.br';
const COMPANY = 'Jonatas Castro Tech Services Serviços de Informática EIRELI';
const CNPJ = '39.246.817/0001-80';

type Section = {
  id: string;
  title: string;
  content?: string | null;
  subsections?: { subtitle: string; text: string }[];
  list?: string[];
  footer?: string;
};

const SECTIONS: Section[] = [
  { id: 'introducao', title: '1. Introdução', content: `A Nosso Ello ("nós", "nosso" ou "plataforma") respeita sua privacidade e está comprometida com a proteção dos seus dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018) e demais legislações aplicáveis.\n\nEsta Política descreve quais dados coletamos, como os utilizamos, com quem os compartilhamos e quais são seus direitos como titular.\n\nAo utilizar a Nosso Ello, você concorda com os termos desta Política. Se não concordar, não utilize nossos serviços.` },
  { id: 'responsavel', title: '2. Controlador dos dados', content: `O controlador responsável pelo tratamento dos seus dados é:\n\n**${COMPANY}**\nCNPJ: ${CNPJ}\nE-mail do DPO: ${CONTACT_EMAIL}` },
  { id: 'dados-coletados', title: '3. Dados que coletamos', subsections: [
    { subtitle: '3.1 Dados de cadastro', text: 'Nome completo, endereço de e-mail, foto de perfil e, para terapeutas, CPF ou CNPJ, registro profissional (CRP, quando informado) e dados bancários para repasse (chave PIX).' },
    { subtitle: '3.2 Dados de agendamento e sessão', text: 'Data, horário, modalidade (online ou presencial), duração e status das sessões realizadas ou agendadas. Para sessões online, registramos o identificador da conferência no Google Meet.' },
    { subtitle: '3.3 Dados financeiros', text: 'Valor das sessões, histórico de pagamentos e status de cobrança. Dados de cartão de crédito e chaves PIX são processados e armazenados exclusivamente pelas plataformas de pagamento homologadas que utilizamos — a Nosso Ello não armazena números de cartão.' },
    { subtitle: '3.4 Dados de saúde (sensíveis)', text: 'Prontuários e evoluções de sessão redigidos pelo terapeuta. Esses dados são classificados como dados sensíveis nos termos da LGPD, ficam restritos ao terapeuta responsável e não são acessados pela Nosso Ello, salvo por determinação legal.' },
    { subtitle: '3.5 Dados de uso e acesso', text: 'Endereço IP, tipo de navegador, sistema operacional, páginas visitadas, data e hora de acesso. Coletados automaticamente para fins de segurança, diagnóstico técnico e melhoria da plataforma.' },
    { subtitle: '3.6 Dados de autenticação via Google', text: 'Quando você utiliza o login com Google, recebemos nome, e-mail e foto de perfil conforme autorizado por você no momento da autenticação.' },
  ]},
  { id: 'finalidades', title: '4. Como utilizamos seus dados', list: [
    'Criar e gerenciar sua conta na plataforma.',
    'Viabilizar o agendamento, a realização e o histórico de sessões.',
    'Processar pagamentos e realizar repasses aos terapeutas.',
    'Enviar notificações sobre sessões, agendamentos e atualizações da conta (por e-mail).',
    'Garantir a segurança da plataforma e prevenir fraudes.',
    'Cumprir obrigações legais e regulatórias.',
    'Melhorar continuamente nossos serviços com base em dados agregados e anonimizados.',
  ]},
  { id: 'base-legal', title: '5. Base legal para o tratamento', list: [
    '**Execução de contrato:** tratamento necessário para prestar os serviços contratados.',
    '**Cumprimento de obrigação legal:** dados fiscais, contábeis e regulatórios.',
    '**Consentimento:** dados de saúde (prontuários) e comunicações de marketing, quando aplicável.',
    '**Legítimo interesse:** segurança da plataforma, prevenção de fraudes e melhoria de serviços.',
  ]},
  { id: 'compartilhamento', title: '6. Compartilhamento de dados', content: 'Não vendemos seus dados. Compartilhamos apenas quando necessário para operar a plataforma:', list: [
    '**Entre terapeuta e cliente:** nome e dados básicos de contato para viabilizar a sessão.',
    '**Plataformas de pagamento:** dados necessários para processar cobranças e repasses, com provedores homologados.',
    '**Google:** para autenticação (OAuth) e realização de sessões online (Google Meet e Google Calendar).',
    '**Infraestrutura de e-mail:** para envio de notificações transacionais.',
    '**Autoridades públicas:** quando exigido por lei, ordem judicial ou para proteção de direitos.',
  ], footer: 'Todos os fornecedores são contratualmente obrigados a tratar os dados com segurança e apenas para as finalidades permitidas.' },
  { id: 'retencao', title: '7. Retenção dos dados', content: `Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas nesta Política:\n\n— **Dados de conta:** enquanto a conta estiver ativa. Após encerramento, por até 5 anos para fins legais e fiscais.\n\n— **Dados financeiros e de sessão:** 5 anos, em conformidade com obrigações fiscais e contábeis (Código Tributário Nacional).\n\n— **Prontuários:** conforme regulamentação do Conselho Federal de Psicologia (mínimo de 5 anos após o encerramento do atendimento).\n\n— **Logs de acesso:** 6 meses, conforme exigência do Marco Civil da Internet (Lei nº 12.965/2014).` },
  { id: 'seguranca', title: '8. Segurança dos dados', content: 'Adotamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, perda ou destruição, incluindo:', list: [
    'Comunicação criptografada via HTTPS/TLS.',
    'Acesso restrito aos dados por perfil e função.',
    'Armazenamento em servidores com certificação de segurança reconhecida.',
    'Monitoramento contínuo de acessos e atividades suspeitas.',
  ], footer: 'Nenhum sistema é 100% seguro. Em caso de incidente que possa gerar risco ou dano aos titulares, notificaremos os afetados e a Autoridade Nacional de Proteção de Dados (ANPD) conforme previsto na LGPD.' },
  { id: 'cookies', title: '9. Cookies', content: `Utilizamos cookies e tecnologias semelhantes para manter sua sessão autenticada, memorizar preferências e analisar o uso da plataforma.\n\nVocê pode configurar seu navegador para recusar cookies, mas algumas funcionalidades da plataforma podem não funcionar corretamente sem eles.\n\nNão utilizamos cookies para rastreamento publicitário de terceiros.` },
  { id: 'direitos', title: '10. Seus direitos como titular (LGPD)', content: `Você tem os seguintes direitos em relação aos seus dados pessoais, que podem ser exercidos pelo e-mail ${CONTACT_EMAIL}:`, list: [
    '**Acesso:** confirmar a existência de tratamento e obter cópia dos seus dados.',
    '**Correção:** corrigir dados incompletos, inexatos ou desatualizados.',
    '**Anonimização, bloqueio ou eliminação:** de dados desnecessários ou tratados em desconformidade com a LGPD.',
    '**Portabilidade:** receber seus dados em formato estruturado para uso por outro serviço.',
    '**Revogação do consentimento:** a qualquer momento, para os tratamentos baseados em consentimento.',
    '**Oposição:** ao tratamento realizado com base em legítimo interesse.',
    '**Informação:** sobre com quem seus dados foram compartilhados.',
  ], footer: 'Responderemos às solicitações em até 15 dias úteis.' },
  { id: 'menores', title: '11. Menores de idade', content: 'A Nosso Ello não é destinada a menores de 18 anos. Não coletamos intencionalmente dados de menores. Caso identifiquemos que dados de um menor foram coletados sem consentimento parental adequado, os excluiremos imediatamente.' },
  { id: 'atualizacoes', title: '12. Alterações nesta Política', content: 'Podemos atualizar esta Política periodicamente. Alterações relevantes serão comunicadas por e-mail ou por aviso na plataforma antes de entrarem em vigor. O uso continuado da Nosso Ello após a notificação implica aceite das alterações.' },
  { id: 'contato', title: '13. Contato e DPO', content: `Para dúvidas, solicitações ou reclamações relacionadas ao tratamento dos seus dados, entre em contato com nosso Encarregado de Proteção de Dados (DPO):\n\n**E-mail:** ${CONTACT_EMAIL}\n\nVocê também pode registrar reclamações diretamente na Autoridade Nacional de Proteção de Dados (ANPD): www.gov.br/anpd` },
];

function PrivacidadePage() {
  useRouteMeta({
    title: 'Política de Privacidade',
    description: 'Como a Nosso Ello coleta, usa e protege seus dados pessoais e de saúde em conformidade com a LGPD.',
    canonical: 'https://nossoello.com.br/privacidade',
  });
  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-full px-4 py-1.5 text-teal-700 text-sm font-medium mb-6">
              LGPD · Lei nº 13.709/2018
            </div>
            <h1 className="text-4xl font-extrabold text-slate-800 tracking-tight mb-3">Política de Privacidade</h1>
            <p className="text-slate-500 text-sm">
              Última atualização: <strong>{LAST_UPDATED}</strong>
            </p>
          </div>

          <nav className="bg-white border border-slate-100 rounded-2xl px-6 py-5 mb-10 shadow-sm">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Índice</p>
            <ol className="space-y-1.5">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-sm text-teal-700 hover:text-teal-900 hover:underline transition">
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="space-y-12">
            {SECTIONS.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">
                  {section.title}
                </h2>
                {section.content && (
                  <div className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                    <RichText text={section.content.trim()} />
                  </div>
                )}
                {section.subsections && (
                  <div className="space-y-5 mt-2">
                    {section.subsections.map((sub) => (
                      <div key={sub.subtitle} className="bg-white rounded-xl border border-slate-100 px-5 py-4 shadow-sm">
                        <h3 className="text-sm font-semibold text-slate-700 mb-1.5">{sub.subtitle}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{sub.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {section.list && (
                  <ul className="mt-3 space-y-2">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                        <span className="mt-1.5 size-1.5 rounded-full bg-teal-400 shrink-0" />
                        <RichText text={item} />
                      </li>
                    ))}
                  </ul>
                )}
                {section.footer && (
                  <p className="mt-4 text-sm text-slate-500 italic leading-relaxed">{section.footer}</p>
                )}
              </section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400">{COMPANY} · CNPJ {CNPJ}</p>
            <p className="text-xs text-slate-400 mt-1">
              Dúvidas?{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-600 hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i} className="text-slate-700">{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
